import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import TabellinoModal from "@/components/TabellinoModal";
import { matchLegLabel } from "@/lib/competitions";

/**
 * UpcomingMatches — widget for the Presidente dashboard.
 * Lists scheduled (not-yet-played) matches for the team, league + cups merged.
 * Click a match → open TabellinoModal to insert/edit result.
 */
export default function UpcomingMatches({ teamId, limit = 8, mine = true, canManage = false }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openMatchId, setOpenMatchId] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);

    // Fetch inside the effect via promise callbacks (no synchronous setState),
    // bump reloadKey to refresh after a tabellino is saved.
    useEffect(() => {
        if (!teamId) return undefined;
        let active = true;
        // Vista "mia squadra" (mine): mostra TUTTE le prossime partite (non solo quelle
        // con finestra aperta), così il presidente vede sempre il proprio calendario.
        // Il componente evidenzia quali sono giocabili ora ("Inserisci →") e quali no ("dal …").
        api.get(`/teams/${teamId}/upcoming-matches`, { params: { limit } })
            .then(({ data: d }) => { if (active) setData(d); })
            .catch((e) => { if (active) setError(e.response?.data?.detail || "Errore"); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [teamId, limit, mine, reloadKey]);

    if (!teamId) return null;
    if (loading) {
        return <div className="op-card p-6 mt-6 op-label text-t8f95a3 animate-pulse">// CARICAMENTO PARTITE…</div>;
    }
    if (error) {
        return <div className="op-card p-6 mt-6 text-tff4d4d text-sm">{error}</div>;
    }
    const matches = data?.matches || [];
    const playableNow = matches.filter((m) => m.window_open === true).length;

    return (
        <div className="op-card mt-6 overflow-hidden" data-testid="upcoming-matches">
            <div className="px-5 py-4 border-b border-t22252a bg-t0e1014 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <span className="text-2xl">⚽</span>
                    <div>
                        <div className="op-label text-[10px] text-t2bb6ff">// {mine ? "LE TUE PROSSIME PARTITE" : "PROSSIME PARTITE"}</div>
                        <div className="text-tffffff op-heading text-lg">
                            {data?.total || 0} {((data?.total || 0) === 1) ? "partita" : "partite"}
                            {mine && playableNow > 0 && (
                                <span className="op-label text-[10px] text-t7ce38b ml-2 align-middle">· {playableNow} giocabili ora</span>
                            )}
                        </div>
                    </div>
                </div>
                {playableNow > 0 && mine && (
                    <span className="op-label text-[10px] text-t5c6370">Clicca le verdi per il tabellino</span>
                )}
            </div>

            {canManage && (
                <AbsenceManager teamId={teamId} onChange={() => setReloadKey((k) => k + 1)} />
            )}

            {matches.length === 0 ? (
                <div className="px-5 py-8 text-center">
                    <div className="text-3xl mb-2">🎉</div>
                    <div className="text-t8f95a3 text-sm">{mine
                        ? <>Nessuna partita in programma.<br />Calendario non ancora generato o tutte le partite giocate.</>
                        : <>Nessuna partita in programma.<br />Calendario non ancora generato o tutte le partite giocate.</>}</div>
                </div>
            ) : (
                <ul className="divide-y divide-t16181c">
                    {matches.map((m, idx) => {
                        const canOpen = mine ? m.window_open === true : true;
                        return (
                            <UpcomingRow
                                key={`${m.match_id}-${idx}`}
                                item={m}
                                onOpen={canOpen ? () => setOpenMatchId(m.match_id) : undefined}
                            />
                        );
                    })}
                </ul>
            )}

            {openMatchId && (
                <TabellinoModal
                    matchId={openMatchId}
                    onClose={() => setOpenMatchId(null)}
                    onSaved={() => { setReloadKey((k) => k + 1); }}
                />
            )}
        </div>
    );
}

function UpcomingRow({ item, onOpen }) {
    const homeBold = item.is_home;
    const suspended = item.suspended || [];
    const injured = item.injured || [];
    const pa = item.postponed_absence;
    const hasIssue = suspended.length > 0 || injured.length > 0;
    const clickable = typeof onOpen === "function";
    return (
        <li
            className={`px-5 py-3 transition-colors ${clickable ? "hover:bg-t16181c/60 cursor-pointer" : "cursor-default"} ${hasIssue ? "border-l-4 border-tff4d4d bg-tff4d4d/5" : ""}`}
            onClick={clickable ? onOpen : undefined}
            data-testid={`upcoming-${item.match_id}`}
        >
            <div className="flex items-center gap-3">
                {/* Context badge */}
                <div className="hidden sm:block flex-shrink-0 w-48">
                    <Link
                        to={item.context_link}
                        onClick={(e) => e.stopPropagation()}
                        className="op-label text-[10px] hover:underline truncate block"
                        style={{ color: item.context_color }}
                    >
                        {item.context_label}
                    </Link>
                    {item.leg && (
                        <div className="op-mono text-[9px] text-t5c6370 mt-0.5">{matchLegLabel(item.leg)}</div>
                    )}
                </div>

                {/* Teams */}
                <div className="flex-1 flex items-center gap-2 min-w-0">
                    <div className="flex-1 flex items-center justify-end gap-2 min-w-0">
                        <span className={`text-sm truncate ${homeBold ? "text-tffffff font-semibold" : "text-t8f95a3"}`}>
                            {item.home_team?.name || "—"}
                        </span>
                        {item.home_team?.logo_url && (
                            <img src={item.home_team.logo_url} alt="" className="w-6 h-6 object-contain flex-shrink-0" />
                        )}
                    </div>
                    <div className="op-label text-[10px] text-t5c6370 px-2">VS</div>
                    <div className="flex-1 flex items-center gap-2 min-w-0">
                        {item.away_team?.logo_url && (
                            <img src={item.away_team.logo_url} alt="" className="w-6 h-6 object-contain flex-shrink-0" />
                        )}
                        <span className={`text-sm truncate ${!homeBold ? "text-tffffff font-semibold" : "text-t8f95a3"}`}>
                            {item.away_team?.name || "—"}
                        </span>
                    </div>
                </div>

                {/* Action */}
                <div className="hidden sm:block flex-shrink-0 text-right">
                    {item.date_from && (
                        <div className="op-mono text-[9px] text-t5c6370 mb-0.5" title={item.deadline ? `Termine: ${item.deadline}` : undefined}>
                            {fmtDM(item.date_from)}
                        </div>
                    )}
                    {item.window_open === false && item.date_from ? (
                        <span className="op-label text-[10px] px-2 py-1 border border-t22252a text-t5c6370" title={`Il tabellino si attiva il ${item.date_from}`}>
                            dal {fmtDM(item.date_from)}
                        </span>
                    ) : (
                        <span className="op-label text-[10px] px-2 py-1 border border-t2bb6ff text-t2bb6ff">
                            Inserisci →
                        </span>
                    )}
                </div>
            </div>

            {/* Mobile (verticale): contesto + data/azione, che a destra è nascosta su schermi piccoli */}
            <div className="sm:hidden flex items-center justify-between gap-2 mt-2">
                <Link
                    to={item.context_link}
                    onClick={(e) => e.stopPropagation()}
                    className="op-label text-[10px] truncate min-w-0"
                    style={{ color: item.context_color }}
                >
                    {item.context_label}{item.leg ? ` · ${matchLegLabel(item.leg)}` : ""}
                </Link>
                {item.window_open === false && item.date_from ? (
                    <span className="op-label text-[10px] px-2 py-1 border border-t22252a text-t5c6370 shrink-0" title={`Il tabellino si attiva il ${item.date_from}`}>
                        dal {fmtDM(item.date_from)}
                    </span>
                ) : (
                    <span className="op-label text-[10px] px-2 py-1 border border-t2bb6ff text-t2bb6ff shrink-0">
                        Inserisci →
                    </span>
                )}
            </div>


            {/* Suspended badge */}
            {suspended.length > 0 && (
                <div className="mt-2 ml-0 sm:ml-48 flex items-center gap-2 flex-wrap" data-testid={`suspended-${item.match_id}`}>
                    <span className="op-label text-[10px] px-2 py-0.5 border border-tff4d4d text-tff4d4d">
                        ⚠️ SQUALIFICATI
                    </span>
                    {suspended.map((p) => (
                        <span key={p.pes_id} className="op-mono text-[10px] text-tff4d4d">
                            {p.name} <span className="text-t5c6370">({p.reason})</span>
                        </span>
                    ))}
                </div>
            )}

            {/* Injured badge (shown on first upcoming match only) */}
            {injured.length > 0 && (
                <div className="mt-2 ml-0 sm:ml-48 flex items-center gap-2 flex-wrap" data-testid={`injured-${item.match_id}`}>
                    <span className="op-label text-[10px] px-2 py-0.5 border border-tff7a1a text-tff7a1a">
                        🩹 INFORTUNATI
                    </span>
                    {injured.map((p) => (
                        <span key={p.pes_id} className="op-mono text-[10px] text-tff7a1a">
                            {p.name} <span className="text-t5c6370">({p.weeks_remaining} {p.weeks_remaining === 1 ? "decade" : "decadi"})</span>
                        </span>
                    ))}
                </div>
            )}

            {/* Postponed for absence badge */}
            {pa && (
                <div className="mt-2 ml-0 sm:ml-48 flex items-center gap-2 flex-wrap" data-testid={`absence-${item.match_id}`}>
                    <span className="op-label text-[10px] px-2 py-0.5 border border-tb084ff text-tb084ff">
                        🗓️ IN ASSENZA
                    </span>
                    <span className="op-mono text-[10px] text-tb084ff">
                        {pa.absent_team_name} assente · rinviata al <span className="font-semibold">{fmtDM(item.date_from)}</span>
                        {pa.clamped && <span className="text-t5c6370"> (limite turno di coppa)</span>}
                    </span>
                </div>
            )}
        </li>
    );
}

function fmtDM(iso) {
    if (!iso) return "";
    const [, m, d] = iso.split("-");
    return `${d}/${m}`;
}

function todayISO() {
    return new Date().toISOString().slice(0, 10);
}

function AbsenceManager({ teamId, onChange }) {
    const [list, setList] = useState(null);
    const [open, setOpen] = useState(false);
    const [start, setStart] = useState(todayISO());
    const [end, setEnd] = useState("");
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState("");
    const [err, setErr] = useState("");

    const load = () => {
        api.get(`/teams/${teamId}/absences`)
            .then(({ data }) => setList(data.absences || []))
            .catch(() => setList([]));
    };
    useEffect(() => { if (teamId) load(); /* eslint-disable-next-line */ }, [teamId]);

    const create = async () => {
        setErr(""); setMsg("");
        if (!start || !end) { setErr("Inserisci data inizio e fine."); return; }
        if (end <= start) { setErr("La data di fine deve essere successiva a quella di inizio."); return; }
        setBusy(true);
        try {
            const { data } = await api.post(`/teams/${teamId}/absences`, { start_date: start, end_date: end });
            setMsg(`Assenza registrata: ${data.affected_count} partita/e rinviata/e.`);
            setOpen(false); setEnd("");
            load(); onChange && onChange();
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        } finally { setBusy(false); }
    };

    const cancel = async (id) => {
        if (!window.confirm("Annullare questa assenza? Le partite torneranno alle date originali.")) return;
        setBusy(true); setErr(""); setMsg("");
        try {
            await api.delete(`/teams/${teamId}/absences/${id}`);
            load(); onChange && onChange();
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        } finally { setBusy(false); }
    };

    const reapply = async (id) => {
        setBusy(true); setErr(""); setMsg("");
        try {
            const { data } = await api.post(`/teams/${teamId}/absences/${id}/reapply`);
            setMsg(data.added > 0
                ? `Assenza ri-applicata: ${data.added} nuova/e partita/e rinviata/e (Primavera inclusa).`
                : "Nessuna nuova partita da agganciare: erano già tutte in assenza.");
            load(); onChange && onChange();
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        } finally { setBusy(false); }
    };

    const statusLabel = { scheduled: "programmata", active: "in corso", past: "conclusa" };
    const statusColor = { scheduled: "rgb(var(--tc-b084ff))", active: "rgb(var(--tc-7ce38b))", past: "rgb(var(--tc-5c6370))" };

    return (
        <div className="px-5 py-3 border-b border-t22252a bg-t0b0d10" data-testid="absence-manager">
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="op-label text-[10px] text-tb084ff">// ASSENZA — RINVIO PARTITE</div>
                <button
                    onClick={() => { setOpen((v) => !v); setErr(""); setMsg(""); }}
                    className="op-label text-[10px] px-2.5 py-1 border border-tb084ff text-tb084ff hover:bg-tb084ff/10 transition-colors"
                    data-testid="absence-add-btn"
                >
                    {open ? "Chiudi" : "＋ Metti in assenza"}
                </button>
            </div>

            {open && (
                <div className="mt-3 flex items-end gap-3 flex-wrap" data-testid="absence-form">
                    <div>
                        <div className="op-mono text-[9px] text-t5c6370 mb-1 uppercase">Dal</div>
                        <input type="date" min={todayISO()} value={start} onChange={(e) => setStart(e.target.value)}
                            className="bg-t0f1115 border border-t22252a text-tffffff op-mono text-[12px] px-2 py-1" data-testid="absence-start" />
                    </div>
                    <div>
                        <div className="op-mono text-[9px] text-t5c6370 mb-1 uppercase">Al</div>
                        <input type="date" min={start || todayISO()} value={end} onChange={(e) => setEnd(e.target.value)}
                            className="bg-t0f1115 border border-t22252a text-tffffff op-mono text-[12px] px-2 py-1" data-testid="absence-end" />
                    </div>
                    <button onClick={create} disabled={busy}
                        className="op-label text-[10px] px-3 py-1.5 border border-tb084ff text-tb084ff hover:bg-tb084ff/10 disabled:opacity-40 transition-colors"
                        data-testid="absence-submit">
                        {busy ? "..." : "Conferma assenza"}
                    </button>
                    <span className="op-mono text-[9px] text-t5c6370 max-w-xs leading-tight">
                        Le partite (campionato e coppe) nel periodo slittano in avanti della stessa durata. Annullabile finché non è conclusa.
                    </span>
                </div>
            )}

            {err && <div className="mt-2 op-mono text-[10px] text-tff4d4d" data-testid="absence-err">{err}</div>}
            {msg && <div className="mt-2 op-mono text-[10px] text-t7ce38b" data-testid="absence-msg">{msg}</div>}

            {list && list.length > 0 && (
                <div className="mt-3 space-y-1.5">
                    {list.map((a) => (
                        <div key={a.id} className="flex items-center justify-between gap-2 border border-t16181c px-2.5 py-1.5" data-testid={`absence-item-${a.id}`}>
                            <div className="op-mono text-[10px] text-tc9ccd3">
                                {fmtDM(a.start_date)} → {fmtDM(a.end_date)}
                                <span className="text-t5c6370 ml-1">({a.duration_days}g · {a.affected_count} rinviate)</span>
                                <span className="ml-2 uppercase text-[9px]" style={{ color: statusColor[a.status] }}>{statusLabel[a.status]}</span>
                            </div>
                            {a.status !== "past" && (
                                <div className="flex items-center gap-1.5">
                                    <button onClick={() => reapply(a.id)} disabled={busy}
                                        className="op-label text-[9px] px-2 py-0.5 border border-tb084ff text-tb084ff hover:bg-tb084ff/10 disabled:opacity-40 transition-colors"
                                        title="Aggancia le partite non ancora rinviate (es. Primavera) con la stessa regola"
                                        data-testid={`absence-reapply-${a.id}`}>
                                        Ri-applica
                                    </button>
                                    <button onClick={() => cancel(a.id)} disabled={busy}
                                        className="op-label text-[9px] px-2 py-0.5 border border-tff4d4d text-tff4d4d hover:bg-tff4d4d/10 disabled:opacity-40 transition-colors"
                                        data-testid={`absence-cancel-${a.id}`}>
                                        Annulla
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
